// src/components/KraVisibilitySettings.js (Checkbox version)
import React, { useState, useEffect } from 'react';
import { AlertBar, CircularLoader, Checkbox, colors } from '@dhis2/ui';
import { useKraVisibility } from '../hooks/useKraVisibility';
import { GiTargeted } from 'react-icons/gi';

const KraVisibilitySettings = () => {
  const { 
    isKraEnabled, 
    loading, 
    saving: globalSaving,
    error: loadError,
    saveKraVisibility, 
    isAdmin,
    orgUnitId 
  } = useKraVisibility();
  
  const [localEnabled, setLocalEnabled] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!loading) {
      setLocalEnabled(isKraEnabled === true);
    }
  }, [isKraEnabled, loading]);

  if (!isAdmin) {
    return (
      <div style={{ padding: '6px 12px', backgroundColor: '#fff3cd', borderRadius: '4px' }}>
        <p style={{ color: '#856404', margin: 0, fontSize: '0.8rem' }}>
          ⚠️ Only administrators can access these settings.
        </p>
      </div>
    );
  }

  // ✅ Handle checkbox change
  const handleChange = (event) => {
    if (isSaving || globalSaving) return;
    
    setSaveError(null);
    setSuccess(false);
    setIsSaving(true);
    
    const newValue = event.target.checked;
    setLocalEnabled(newValue);
    
    saveKraVisibility(newValue)
      .then(() => {
        setSuccess(true);
        setIsSaving(false);
        setTimeout(() => setSuccess(false), 3000);
      })
      .catch((err) => {
        setLocalEnabled(!newValue);
        setSaveError(err.message || 'Failed to save setting');
        setIsSaving(false);
      });
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '8px' }}>
        <CircularLoader small />
        <span style={{ marginLeft: '8px', fontSize: '0.85rem' }}>Loading...</span>
      </div>
    );
  }

  const isSavingState = isSaving || globalSaving;

  return (
    <div style={{
      padding: '8px 12px',
      backgroundColor: '#f8f9fa',
      borderRadius: '4px',
      border: '1px solid #e0e0e0',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '8px',
      }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <GiTargeted size={16} style={{ color: '#2c6693' }} />
            <strong style={{ fontSize: '0.85rem', color: '#2c6693' }}>Key Performance Area (KPA) Visibility</strong>
          </div>
          <p style={{ 
            margin: '2px 0 0 0', 
            fontSize: '0.75rem', 
            color: localEnabled ? '#2e7d32' : '#666'
          }}>
            {localEnabled 
              ? '✅ Staff can view and manage KPAs' 
              : '❌ Staff cannot view or manage KPAs (Key Performance Indicators remain visible)'
            }
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {isSavingState && (
            <CircularLoader small />
          )}
          
          {/* ✅ Checkbox - No form submission issues */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <input
              type="checkbox"
              id="kraVisibilityToggle"
              checked={localEnabled}
              onChange={handleChange}
              disabled={isSavingState}
              style={{
                width: '18px',
                height: '18px',
                cursor: isSavingState ? 'not-allowed' : 'pointer',
                accentColor: '#4caf50',
              }}
            />
            <label 
              htmlFor="kraVisibilityToggle"
              style={{
                fontSize: '0.75rem',
                color: localEnabled ? '#2e7d32' : '#666',
                cursor: isSavingState ? 'not-allowed' : 'pointer',
                fontWeight: '500',
              }}
            >
              {localEnabled ? 'Enabled' : 'Disabled'}
            </label>
          </div>
        </div>
      </div>

      {loadError && (
        <AlertBar critical style={{ marginTop: '6px', fontSize: '0.8rem' }}>
          {loadError}
        </AlertBar>
      )}

      {saveError && (
        <AlertBar critical style={{ marginTop: '6px', fontSize: '0.8rem' }}>
          {saveError}
        </AlertBar>
      )}

      {success && (
        <AlertBar success style={{ marginTop: '6px', fontSize: '0.8rem' }}>
          ✅ Setting saved successfully!
        </AlertBar>
      )}

      <div style={{
        marginTop: '6px',
        padding: '4px 8px',
        backgroundColor: '#e8f5e9',
        borderRadius: '3px',
        fontSize: '0.7rem',
        color: '#2e7d32'
      }}>
        💡 KPAs are optional. Key Performance Indicators (KPIs) are <strong>mandatory</strong> and always visible.
      </div>
    </div>
  );
};

export default KraVisibilitySettings;